import React, { useState, useEffect } from 'react';
import { Box, Checkbox, Typography, Paper } from '@mui/material';

interface SelectableItem {
  [key: string]: any; 
}

interface SelectableCheckboxProps {
  item: SelectableItem;
  isSelected: boolean;
  onToggle: (itemKeyValue: any) => void;
  displayKey: string;
}

export const SelectableCheckbox: React.FC<SelectableCheckboxProps> = ({
  item,
  isSelected,
  onToggle,
  displayKey,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '4px',
        minWidth: '180px',
        backgroundColor: isSelected ? 'rgba(0, 150, 136, 0.08)' : 'transparent',
        '&:hover': {
          backgroundColor: isSelected
            ? 'rgba(0, 150, 136, 0.12)'
            : 'rgba(0, 0, 0, 0.04)',
          cursor: 'pointer',
        },
        transition: 'background-color 0.2s ease',
      }}
      onClick={() => onToggle(item[displayKey])}
    >
      <Checkbox
        checked={isSelected}
        color="primary"
        sx={{
          padding: '2px',
          marginRight: '8px',
          '&.Mui-checked': {
            color: '#009688',
          },
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: isSelected ? 500 : 400 }}>
        {item.name}
      </Typography>
    </Box>
  );
};

interface MultiSelectProps {
  title?: string;
  items: SelectableItem[];
  selectedItems?: any[];
  setSelectedItems?: (selected: any[]) => void;
  itemKey?: string; 
  showBorder?: boolean;
  error?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  title = 'Choose Items',
  items,
  selectedItems,
  setSelectedItems,
  itemKey = 'id',
  showBorder = false,
  error,
}) => {
  const [internalSelectedItems, setInternalSelectedItems] = useState<any[]>(
    selectedItems || []
  );

  useEffect(() => {
    if (selectedItems) {
      setInternalSelectedItems(selectedItems);
    }
  }, [selectedItems]);

  const handleToggle = (itemKeyValue: any) => {
    const isAlreadySelected = internalSelectedItems.includes(itemKeyValue);
    const updatedSelection = isAlreadySelected
      ? internalSelectedItems.filter((id) => id !== itemKeyValue)
      : [...internalSelectedItems, itemKeyValue];

    setInternalSelectedItems(updatedSelection);
    if (setSelectedItems) {
      setSelectedItems(updatedSelection);
    }
  };

  return (
    <Box>
      {title && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            marginBottom: '10px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {title}
        </Typography>
      )}
      <Paper
        elevation={0}
        sx={{
          border: showBorder ? '1px solid rgba(0, 0, 0, 0.23)' : 'none',
          borderRadius: '4px',
          padding: '12px',
        }}
      >
        <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
          {items.map((item) => (
            <SelectableCheckbox
              key={item[itemKey]}
              item={item}
              isSelected={internalSelectedItems.includes(item[itemKey])}
              onToggle={handleToggle}
              displayKey={itemKey}
            />
          ))}
        </Box>
      </Paper>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};
